import type { OperatorTabletWorkspace } from "./useOperatorTabletWorkspace";
import { dateTime } from "../production-time";
import { OperatorProductionOverview } from "../OperatorProductionOverview";
import { CasePackagingStatus } from "../CasePackagingStatus";
import { OperatorMachineStatus } from "../OperatorMachineStatus";
import { OperatorCompletedResult } from "../OperatorCompletedJobs";
import { TechnicalCallButton } from "../TechnicalCallButton";
import { AlertTriangle, CheckCircle2, Clock3, Gauge, MessageSquareWarning, PauseCircle, PlayCircle, Plus, X } from "lucide-react";
import { hasVisibleWorkOrder } from "../work-order-display";
import { operatorConnectionLabel } from "../operator-connection";
import { counterSyncLabel } from "../live-counter";
import { operatorDowntimeDraftKey } from "../operator-drafts";
import { ProductNoteButton } from "../ProductNoteButton";
import { OpenDowntimeReason } from "../OpenDowntimeReason";
import { operatorNumber, operatorAmount } from "./quantity-format";
import { StatusBadge } from "./OperatorRunStatus";
import { operatorDuration } from "../production-time";
import { productionIdentityLabel as operatorRunLabel } from "../work-order-display";
import { operatorProductionSourceLabel as operatorWorkOrderStatus } from "../work-order-display";

export function OperatorCurrentJob({ selected, openDowntime, liveCounter, terminalReconnecting, loadError, updatedAt, totalPlannedCases, productNote, isShiftCarryover, timers, saving, setDowntimeConfirmOpen, setMaterialsOpen, selectedPlanNote, acknowledgePlanNote, operationTerminalCode, timeline, caseProgress, usesCompletedCases, machineComparison, actualRatePerMinute, usesMachineSpeed, speedUnit, scrapGroups, setRecordsOpen, scrapLabel, projectedFinishAt, setScrapSheet, setWorkOrdersOpen, reference, openReasonRequired, visibleDowntimeNote, resumeProduction, terminalContext, recentReasonIds, user, assignOpenReason, machineConfiguredForDisplay, setMachineCountOpen, setEntryType, setComplaintOpen, hasPendingRunQuantity, openCompletionCheck, saveFailed, loadErrorCode, lastSavedAt, missingDowntimeReasons, visibleDowntimeReason, requestMissingReason, casePackagingEligible, productPackaging, manualPackaging, quantityUnits, setCasePackagingRunId }: OperatorTabletWorkspace["currentJob"]) {
  if (!selected) return null;
  const packagingAction = casePackagingEligible ? <CasePackagingStatus product={productPackaging} confirmation={manualPackaging.value}
    loading={quantityUnits.loading || manualPackaging.loading} error={manualPackaging.error}
    disabled={terminalReconnecting || loadError} onOpen={() => setCasePackagingRunId(selected?.id ?? null)}
    onRetry={() => { quantityUnits.retry(); manualPackaging.retry(); }} /> : null;
  return (
    <>
      <section
        className={`operator-job-card is-${selected.status.toLowerCase()} ${openDowntime ? "is-stopped" : ""}`}
      >
        {selected.status !== "COMPLETED" ? <header
          className={`operator-job-heading is-${selected.status.toLowerCase()}${selected.status === "ACTIVE" && selected.planItemKind !== "ACTIVITY" ? " operator-case-heading" : ""}`}
        >
          <div className="operator-job-identity">
            {selected.status === "ACTIVE" && selected.planItemKind !== "ACTIVITY" ? <OperatorMachineStatus running={liveCounter.machine.running} measuredAt={liveCounter.counter?.machineMeasuredAt ?? null} lastRunning={liveCounter.counter?.machineRunning ?? null} connected={!terminalReconnecting && !loadError} updatedAt={updatedAt} /> : null}
            <h1>{selected.status === "ACTIVE" ? selected.productName : operatorRunLabel(selected)}</h1>
            <div className="operator-job-meta">
              <strong>{selected.status === "ACTIVE" ? operatorWorkOrderStatus(selected) : hasVisibleWorkOrder(selected) ? selected.productName : operatorWorkOrderStatus(selected)}</strong>
              <span>Ürün&nbsp; {selected.productCode}</span>
              {selected.status === "ACTIVE" && totalPlannedCases != null ? <span>İş emri miktarı {operatorAmount(totalPlannedCases, "koli")}</span> : null}
            </div>
            {productNote ? <div className="operator-product-note-summary"><strong>Ürün notu <small>· {productNote.sourceLabel}</small></strong><p>{productNote.note}</p></div> : null}
          </div>
          <div className={`operator-live-summary${selected.status === "ACTIVE" && !openDowntime ? " has-work-pause" : ""}`}>
            {isShiftCarryover ? (
              <div className="operator-shift-carryover" role="status" aria-label={`Önceki vardiyadan devam. ${selected.shiftCode}. Üretim sayacı devam ediyor.`}>
                <Clock3 size={16} aria-hidden="true" />
                <strong>Önceki vardiyadan</strong>
                <span>Sayaç devam ediyor</span>
              </div>
            ) : null}
            <div
              className={`operator-live-state is-${openDowntime ? "stopped" : "active"}`}
            >
              {selected.status === "ACTIVE" && !openDowntime && selected.planItemKind !== "ACTIVITY"
                ? <span className="operator-work-state-label">İş açık</span>
                : <StatusBadge run={selected} stopped={Boolean(openDowntime)} />}
              <span className="operator-production-timer" aria-label={`${openDowntime ? "Net üretim süresi" : "Üretim süresi"} ${timers.production}`}>
                {openDowntime ? <small className="operator-production-timer-label">Net üretim</small> : null}
                {timers.production}
              </span>
            </div>
            {selected.status === "ACTIVE" && !openDowntime ? (
              <button type="button" className="operator-work-pause" disabled={saving} onClick={() => setDowntimeConfirmOpen(true)}>
                <PauseCircle size={20} aria-hidden="true" />
                <span>{selected.planItemKind === "ACTIVITY" ? "Faaliyeti duraklat" : "İşi duraklat"}</span>
              </button>
            ) : null}
            <div className="operator-technical-action">
              <button className="operator-materials-open" type="button" onClick={() => setMaterialsOpen(true)}>Reçete ve stoklar</button>
              {selectedPlanNote ? <ProductNoteButton
                key={`note-${selected.id}`}
                note={selectedPlanNote}
                sourceLabel={productNote?.sourceLabel}
                productName={selected.productName}
                acknowledgementRequired={selected.status !== "ACTIVE" && !selected.planNoteAcknowledgedAt}
                acknowledgedBy={selected.planNoteAcknowledgedAt ? selected.planNoteAcknowledgedByName : null}
                saving={saving}
                onAcknowledge={() => void acknowledgePlanNote()}
              /> : null}
              {selected.status === "ACTIVE" ? <TechnicalCallButton key={`technical-${selected.id}-${operationTerminalCode}`} runId={selected.id} lineName={selected.lineName} terminalCode={operationTerminalCode} /> : null}
            </div>
          </div>
        </header> : null}
        {selected.status === "ACTIVE" && !openDowntime && selected.planItemKind !== "ACTIVITY" ? (
          <section className="operator-production-panel" aria-label="Üretim özeti">
            {timeline ? (
              <OperatorProductionOverview
                progress={caseProgress}
                packagingAction={usesCompletedCases ? undefined : packagingAction}
                retained={usesCompletedCases ? liveCounter.completedCases.retained : Boolean(terminalReconnecting || loadError || (machineComparison && liveCounter.telemetry.countRetained))}
                qualityLabel={usesCompletedCases ? liveCounter.completedCases.label : machineComparison ? liveCounter.telemetry.countLabel : updatedAt ? `Son sunucu doğrulaması · ${dateTime(new Date(updatedAt).toISOString())}` : "Sunucu kaydı henüz doğrulanmadı"}
                rate={{ value: actualRatePerMinute == null ? null : operatorNumber(actualRatePerMinute, usesMachineSpeed ? 0 : 2), unit: `${speedUnit === "plan birimi" ? "birim" : speedUnit}/dk`, target: null, label: usesMachineSpeed ? liveCounter.telemetry.speedLabel : "İş ortalaması", retained: liveCounter.telemetry.speedRetained }}
                timeLabel="Üretim süresi"
                timeValue={operatorDuration(timeline.productionMilliseconds)}
                downtime={operatorDuration(timeline.downtimeMilliseconds)}
                scrap={scrapGroups && scrapGroups.length > 1 ? <button type="button" className="operator-scrap-detail" onClick={() => setRecordsOpen(true)} aria-label={`Iskarta ayrıntıları: ${scrapLabel}`}>{scrapGroups.length} birim · Ayrıntı</button> : scrapLabel}
                finish={projectedFinishAt ? dateTime(projectedFinishAt) : (usesCompletedCases ? liveCounter.completedCases.retained : machineComparison && liveCounter.telemetry.countRetained) ? "Ölçüm güncel değil" : "Hesaplanıyor"}
              />
            ) : <p className="operator-chart-empty">İş başlatıldığında üretim özeti burada görünecek.</p>}
            {selected.unit === "plan birimi" ? <p className="operator-unit-warning">Plan miktarının birimi doğrulanmadı.</p> : null}
          </section>
        ) : null}
        {selected.status === "COMPLETED" ? <OperatorCompletedResult onScrap={() => setScrapSheet("CHOICE")} onMaterials={() => setMaterialsOpen(true)} run={selected} onRecords={() => setRecordsOpen(true)} onStart={() => setWorkOrdersOpen(true)} startDisabled={loadError || !reference} /> : null}
      </section>
      {selected.status === "ACTIVE" ? (
        <>
          {openDowntime ? (
            <section className={`operator-stop-state operator-downtime-workspace${openReasonRequired && !openDowntime.reasonCodeId && !openDowntime.reasonName ? " has-reason-form" : ""}`} aria-label="Devam eden duruş">
              <div className="operator-stop-panel">
                <div className="operator-downtime-status"><PauseCircle size={22} /><strong>Duruş sürüyor</strong></div>
                <span className="operator-downtime-time-label">Geçen süre</span>
                <span className="operator-stop-counter">
                  {timers.downtime}
                </span>
                {openDowntime.reasonName ? <strong className="operator-downtime-saved-reason">{openDowntime.reasonName}</strong> : null}
                {visibleDowntimeNote ? (
                  <p className="operator-stop-note">
                    <small>AÇIKLAMA</small>
                    {visibleDowntimeNote}
                  </p>
                ) : null}
                <button
                  disabled={saving}
                  onClick={resumeProduction}
                >
                  <PlayCircle size={22} />
                  {saving
                    ? "Kaydediliyor…"
                    : "Üretime devam et"}
                </button>
              </div>
              {openReasonRequired && !openDowntime.reasonCodeId && !openDowntime.reasonName ? <OpenDowntimeReason key={operatorDowntimeDraftKey(operationTerminalCode, terminalContext.session?.operatorId ?? "", terminalContext.session?.id ?? "", terminalContext.terminal.lineId ?? "", selected.id, `downtime:${openDowntime.id}`)} reasons={reference?.reasons ?? []} recentReasonIds={recentReasonIds} draftKey={operatorDowntimeDraftKey(operationTerminalCode, terminalContext.session?.operatorId ?? "", terminalContext.session?.id ?? "", terminalContext.terminal.lineId ?? "", selected.id, `downtime:${openDowntime.id}`)} preview={Boolean(user.preview)} submit={assignOpenReason} /> : null}
            </section>
          ) : (
            <section
              className={`operator-action-grid has-work-controls${selected.planItemKind === "ACTIVITY" ? " is-activity" : ""}`}
              aria-label="Üretim hareketleri"
            >
              <button
                className="operator-action is-production"
                hidden={selected.planItemKind === "ACTIVITY"}
                aria-label={machineConfiguredForDisplay ? "Makine sayacı ayrıntılarını aç" : "Üretim kaydı ekle"}
                onClick={() => machineConfiguredForDisplay ? setMachineCountOpen(true) : setEntryType("PRODUCTION")}
              >
                {machineConfiguredForDisplay ? <Gauge /> : <Plus />}
                <strong>{machineConfiguredForDisplay ? "Sayaç" : "Üretim"}</strong>
              </button>
              <button
                className="operator-action is-scrap"
                hidden={selected.planItemKind === "ACTIVITY"}
                aria-label="Iskarta kaydı ekle"
                onClick={() => setScrapSheet("CHOICE")}
              >
                <X />
                <strong>Iskarta</strong>
              </button>
              <button
                className="operator-action is-complaint"
                aria-label="Şikayet bildir"
                onClick={() => setComplaintOpen(true)}
              >
                <MessageSquareWarning />
                <strong>Şikayet</strong>
              </button>
              <button
                className="operator-action is-complete"
                aria-label="İşi tamamlama kontrolünü aç"
                disabled={saving || hasPendingRunQuantity}
                title={hasPendingRunQuantity ? "Bekleyen miktar kayıtları sonuçlanmadan iş tamamlanamaz." : undefined}
                onClick={() => void openCompletionCheck()}
              >
                <CheckCircle2 />
                <strong>{selected.planItemKind === "ACTIVITY" ? "Etkinliği tamamla" : "İşi tamamla"}</strong>
              </button>
            </section>
          )}
          <div className="operator-secondary-actions">
            <span
              className={`operator-sync-state${saving ? " is-saving" : saveFailed || (usesCompletedCases ? liveCounter.completedCases.value === null : liveCounter.status.warning && !liveCounter.telemetry.countRetained) ? " is-error" : ""}`}
            >
              {saving
                ? "Sunucuya kaydediliyor…"
                : saveFailed
                  ? "Son işlem kaydedilemedi · Tekrar deneyin"
                  : operatorConnectionLabel(terminalReconnecting, loadErrorCode, updatedAt) ?? (
                    !openDowntime && machineConfiguredForDisplay && selected.planItemKind !== "ACTIVITY"
                      ? (usesCompletedCases ? liveCounter.completedCases.label : liveCounter.telemetry.countRetained ? liveCounter.telemetry.countLabel : counterSyncLabel(liveCounter.counter, liveCounter.status))
                      : lastSavedAt
                        ? `Tüm kayıtlar kaydedildi · ${lastSavedAt.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}`
                        : "Henüz yeni kayıt yok")}
            </span>
            {!openDowntime && missingDowntimeReasons.length > 0 && !visibleDowntimeReason ? (
              <button
                type="button"
                className="operator-missing-downtime-reason"
                onClick={requestMissingReason}
              >
                <AlertTriangle size={17} aria-hidden="true" />
                {missingDowntimeReasons.length === 1
                  ? "Eksik duruş nedenini gir"
                  : `${missingDowntimeReasons.length} duruş nedeni eksik`}
              </button>
            ) : null}
          </div>
        </>
      ) : null}
    </>
  );
}
